/**
 * Loading state for the idea dashboard.
 *
 * The user waits on the REAL screen with placeholders, rather than on a
 * separate spinner page. Two reasons this shape was chosen over a dedicated
 * loading screen:
 *
 *   - No navigation jump. The screen they wait on is the screen they end up
 *     on, so the result feels like it resolved rather than replaced.
 *   - Panels that are ready stay useful instead of hiding behind one spinner.
 *
 * HONESTY RULE: a skeleton is shown until the REAL value exists. Nothing here
 * ever renders a placeholder number, a fabricated percentile, or an invented
 * cohort curve — the animation is motion, never data. The only thing these
 * components claim is "not yet".
 */
import { useEffect, useRef, useState } from 'react';

/** Shimmering placeholder block. */
export function Skeleton({
  height,
  width,
  style,
}: {
  height: number | string;
  width: number | string;
  style?: React.CSSProperties;
}) {
  return (
    <span
      aria-hidden="true"
      className="ib-skeleton"
      style={{
        display: 'block',
        height,
        width,
        borderRadius: 5,
        background: 'var(--gutter)',
        position: 'relative',
        overflow: 'hidden',
        ...style,
      }}
    />
  );
}

/**
 * Types text out character by character.
 *
 * Used only for the generated queries: they are the genuinely authored part of
 * the scan, and watching them be written communicates "these were made for
 * you" in a way that a block of text appearing does not.
 *
 * Deliberately fast (2 chars per frame). A slow typewriter is charming once and
 * irritating every time after.
 */
export function TypeOut({
  text,
  onDone,
  charsPerTick = 2,
  tickMs = 16,
}: {
  text: string;
  onDone?: () => void;
  charsPerTick?: number;
  tickMs?: number;
}) {
  const [shown, setShown] = useState(0);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    setShown(0);
    // Respect the user's motion preference: typing is decorative, and someone
    // who asked for reduced motion should get the finished text at once.
    const reduced = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)',
    )?.matches;
    if (reduced) {
      setShown(text.length);
      doneRef.current?.();
      return;
    }

    const id = setInterval(() => {
      setShown((n) => {
        const next = n + charsPerTick;
        if (next >= text.length) {
          clearInterval(id);
          doneRef.current?.();
          return text.length;
        }
        return next;
      });
    }, tickMs);
    return () => clearInterval(id);
  }, [text, charsPerTick, tickMs]);

  const complete = shown >= text.length;
  return (
    <>
      {text.slice(0, shown)}
      {!complete && (
        <span
          aria-hidden="true"
          style={{
            display: 'inline-block',
            width: 6,
            height: '1em',
            verticalAlign: '-2px',
            marginLeft: 1,
            background: 'var(--acc)',
            animation: 'ibBlink .8s step-end infinite',
          }}
        />
      )}
    </>
  );
}
